/* eslint-env browser */
// Customer / driver details › Donkey Miles (wave 3, W2_Portal_Tables look):
// the miles balance as a figure tile, then the miles history in the modern
// table. Read-only. Data, as before: the user record handed in by the
// details page and ONE read of this person's miles history
// (fetchUserMilesHistory) when the tab opens or the record changes.
// Exports (CSV and print) for admin and finance, as before. "← Back" is the
// page's own (top left).
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import { api } from 'common';
import DashboardCard from '../components/DashboardCard';
import { DataTable, StatusChip, calmCaps, formatNumber } from '../components/modern';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

const UserMilesDetails = (props) => {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const { fetchUserMilesHistory } = api;
  const auth = useSelector((state) => state.auth);
  const milesHistory = useSelector((state) => state.auth.milesHistory);
  const dispatch = useDispatch();
  // Which person's history has arrived (the store slot may still hold the
  // person opened before): grey rows until it has.
  const [loadedFor, setLoadedFor] = useState(null);

  useEffect(() => {
    let alive = true;
    const uid = data?.id;
    Promise.resolve(dispatch(fetchUserMilesHistory(uid))).finally(() => {
      if (alive) setLoadedFor(uid === undefined ? null : uid);
    });
    return () => {
      alive = false;
    };
  }, [data, dispatch, fetchUserMilesHistory]);

  const role = auth.profile && auth.profile.usertype;
  const canExport = role === 'admin' || role === 'finance';
  const rows = Array.isArray(milesHistory) ? milesHistory : [];
  const loading = loadedFor === null || loadedFor !== (data?.id === undefined ? null : data.id);
  const title = calmCaps(t('donkey_miles_history', { defaultValue: 'Donkey Miles history' }));
  const milesWord = t('miles', { defaultValue: 'Miles' });

  const actionStatus = (row) => {
    if (row.action === 'credit') return { tone: 'success', icon: <AddRoundedIcon />, label: t('credit', { defaultValue: 'Credit' }) };
    if (row.action === 'debit') return { tone: 'error', icon: <RemoveRoundedIcon />, label: t('debit', { defaultValue: 'Debit' }) };
    return { tone: 'error', icon: <SyncAltRoundedIcon />, label: row.action ? String(row.action) : 'N/A' };
  };

  const columns = [
    { id: 'date', title: t('requestDate'), field: 'date', type: 'datetime' },
    { id: 'milesEarned', title: t('milesEarned'), field: 'milesEarned', type: 'number', render: (row) => formatNumber(row.milesEarned || 0) },
    { id: 'msg', title: t('message_text', { defaultValue: 'Message' }), field: 'msg', render: (row) => row.msg || '-' },
    { id: 'details', title: t('details'), field: 'details', render: (row) => row.details || '-' },
    {
      id: 'action',
      title: t('action'),
      field: 'action',
      render: (row) => {
        const st = actionStatus(row);
        return <StatusChip tone={st.tone} icon={st.icon} label={st.label} />;
      },
    },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 360px)' } }}>
        <DashboardCard
          icon={<StarsOutlinedIcon />}
          hue="amber"
          title={t('donkey_miles_balance')}
          value={`${data?.donkeyMiles !== undefined ? formatNumber(data.donkeyMiles) : 0} ${milesWord}`}
        />
      </Box>
      <DataTable
        title={title}
        rows={loading ? [] : rows}
        columns={columns}
        loading={loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('miles_history_count', {
                  count: total,
                  n: fmt(total),
                  defaultValue_one: '{{n}} entry',
                  defaultValue_other: '{{n}} entries',
                  defaultValue: '{{n}} entries',
                })
        }
        prefsKey="user_miles_history"
        exportCsv={canExport}
        exportPrint={canExport}
        exportFileName="donkey_miles_history"
        exportTitle={title}
        exportSubtitle={`${data?.firstName || ''} ${data?.lastName || ''}`.trim()}
        minWidth={720}
        maxHeight={null}
        emptyState={({ query }) =>
          query ? null : { icon: <LoyaltyOutlinedIcon />, title: t('miles_history_empty', { defaultValue: 'No Donkey Miles yet' }) }
        }
      />
    </Box>
  );
};

export default UserMilesDetails;
